import * as R from 'ramda'
import { getToday } from '../../libs/date'
import { format } from 'date-fns'
import {
  ADDRESS_STATES,
  SHIPPING_WITH_INSURANCE_MERCHAND_CODE,
  SHIPPING_WITH_REFUND_CODE,
  SHIPPING_WITH_RETURN,
  SHIPPING_WITH_SLIP_SCAN,
  SHIPPING_WITH_PUDO,
  SHIPPING_WITH_ORIGIN_PUDO,
  ADDRESS_SOURCES,
} from '../../common/constants'
import {
  createDestinFullAddress,
  createFullAddress,
} from '../../ui/views/shippingSend/Step5Summary'

import { PICKUP_OPTIONS } from './Step1Origin/Pickup'

const getAdditionals = (
  additionalServices,
  isPudo,
  organicPointCode,
  isOriginPudo,
) => {
  const additionals = []
  if (additionalServices.withRefund) {
    additionals.push({
      additional_code: SHIPPING_WITH_REFUND_CODE,
      additional_value: additionalServices.refundAmount,
      additional_flag: true,
      additional_text: '',
    })
  }

  if (additionalServices.insuranceMerchandise) {
    additionals.push({
      additional_code: SHIPPING_WITH_INSURANCE_MERCHAND_CODE,
      additional_value: additionalServices.insuranceValue,
      additional_flag: true,
      additional_text: '',
    })
  }

  if (additionalServices.withReturn) {
    additionals.push({
      additional_code: SHIPPING_WITH_RETURN,
      additional_value: 0,
      additional_flag: true,
      additional_text: '',
    })
  }

  if (additionalServices.scanClientSlip) {
    additionals.push({
      additional_code: SHIPPING_WITH_SLIP_SCAN,
      additional_value: 0,
      additional_flag: true,
      additional_text: '',
    })
  }
  if (isPudo) {
    additionals.push({
      additional_code: SHIPPING_WITH_PUDO,
      additional_value: 0,
      additional_flag: true,
      additional_text: organicPointCode.toString(),
      additional_sub_code: 'EDP',
    })
  }
  if (isOriginPudo) {
    additionals.push({
      additional_code: 'ERE',
      additional_value: 0,
      additional_flag: true,
      additional_text: '',
      additional_sub_code: SHIPPING_WITH_ORIGIN_PUDO,
    })
  }

  return additionals
}

const getShipmentPackages = packages =>
  packages
    .filter(
      p =>
        !R.isNil(p.weight) ||
        !R.isNil(p.length) ||
        !R.isNil(p.width) ||
        !R.isNil(p.height),
    )
    .map(current => ({
      item_length_declared: current.length ?? 0,
      item_width_declared: current.width ?? 0,
      item_height_declared: current.height ?? 0,
      item_weight_declared: current.weight,

      // Not available
      item_comments: '',
      item_synonym_code: '',
      item_external_code: '',
    }))

const getPackagesTotalWeight = packages =>
  packages.reduce((acc, current) => acc + current.weight, 0)

export const isOriginNewAddress = addressSource =>
  addressSource === ADDRESS_SOURCES.newAddress

export const isDestinNewAddress = addressSource =>
  addressSource === ADDRESS_STATES.NEW

const isDestinPickupPoint = addressSource =>
  addressSource === ADDRESS_STATES.AT_PICKUP_POINT

const isESWithoutCanaryIslands = (countryCode, postalCode) =>
  countryCode === 'ES' &&
  !(postalCode.substr(0, 2) === '38' || postalCode.substr(0, 2) === '35')

const formatSubmitData = (values, auditData) => {
  const optionalShippingValues = {}
  const originAddressSource = values.from.addressSource
  const destinAddressSource = values.to.addressState

  if (
    isOriginNewAddress(originAddressSource)
      ? values.from.address.email !== ''
      : values.from.selectedAddress.shippingAddress.email
  ) {
    optionalShippingValues.sender_email_notify_address =
      values.from.address.email
  }

  if (
    isOriginNewAddress(originAddressSource) &&
    values.from.address.email !== ''
  ) {
    optionalShippingValues.sender_email_notify_address =
      values.from.address.email
  }

  if (
    !isOriginNewAddress(originAddressSource) &&
    values.from.selectedAddress.shippingAddress.email !== ''
  ) {
    optionalShippingValues.sender_email_notify_address =
      values.from.selectedAddress.shippingAddress.email
  }

  if (
    isOriginNewAddress(originAddressSource) ||
    values.from.addressSource === 'pickUpPoint'
  ) {
    if (values.from.address.phone !== '') {
      const phoneWithPrefix = `${values.from.address.phonePrefix}${values.from.address.phone}`
      optionalShippingValues.sender_phones = [phoneWithPrefix]
    }
  } else {
    if (values.from.selectedAddress.shippingAddress.phone !== '') {
      const phoneWithPrefix = `${values.from.address.phonePrefix}${values.from.selectedAddress.shippingAddress.phone}`
      optionalShippingValues.sender_phones = [phoneWithPrefix]
    }
  }

  if (values.to.address.email !== '') {
    optionalShippingValues.recipient_email_notify_address =
      values.to.address.email
  }

  if (values.to.address.phone !== '') {
    const destinPhone = isDestinNewAddress(destinAddressSource)
      ? `${values.to.address.phonePrefix}${values.to.address.phone}`
      : values.to.address.phone

    optionalShippingValues.recipient_phones = [destinPhone]
  }

  if (values.from.addressSource === 'pickUpPoint') {
    optionalShippingValues.sender_email_notify_address = values.from.address.email
    optionalShippingValues.recipient_email_notify_address = values.to.address.email?.length
      ? values.to.address.email
      : values.from?.imputationCenter?.shippingAddress?.email
  }

  if (values.shipment.referenceNumber !== '') {
    optionalShippingValues.client_references = [values.shipment.referenceNumber]
  }

  if (
    !isESWithoutCanaryIslands(
      values.to.address.country.code,
      values.to.address.postalCode,
    )
  ) {
    optionalShippingValues.customs_cost_type_code =
      values.shipment.customsSelected.value
  }

  if (values.shipment.hasCustom?.custom) {
    const combinedArrayDocuments = [
      ...(values.customs?.additionalDocumentation || []),
      values?.customs?.generateReportPDF,
    ]
    optionalShippingValues.export_info = {
      allows_electronic_transfer_documents:
        values.shipment.hasCustom?.allowsElectronicalTradeDocuments,
      electronic_documents: combinedArrayDocuments?.map(element => {
        return {
          resource_provider: element?.resource_provider,
          resource_id: element?.resource_id,
          file_name: element?.file.name,
          file_extension: element?.fileBodyPOST.file_extension,
          file_size: element?.file.size,
          file_type: element?.fileBodyPOST?.file_type,
          md5: element?.fileBodyPOST.md5,
          compression_type: 'None',
        }
      }),
      has_custom: values.shipment.hasCustom?.custom,
      shipper_type: values.customs.senderType,
      shipper_name: values.customs?.senderInfo?.companyName ?? undefined,
      shipper_tax_id:
        values.customs.senderType !== 'BUSINESS_UNION'
          ? values.customs?.senderInfo?.dni
          : values.customs?.senderInfo?.nifIva,
      shipping_reason: values.customs.senderInfo?.reason,
      recipient_type: values.customs.receptorType,
      recipient_name: values.customs.receptorInfo?.companyName ?? undefined,
      recipient_tax_id:
        values.customs.receptorType !== 'BUSINESS_UNION'
          ? values.customs?.receptorInfo?.dni
          : values.customs?.receptorInfo?.nifIva,
      articles: values.customs?.inventory?.map(element => {
        return {
          taric_code:
            element.category?.commodity_taric_code ??
            element.category?.commodity_taric_code_other,
          origin_of_goods: element.place?.code,
          nature_of_goods: element.description,
          net_weight_kg:
            getShipmentPackages(values.shipment.packages).reduce(
              (acumulador, current) =>
                acumulador + (current.item_weight_declared || 0),
              0,
            ) / values.customs?.inventory.length,
          units: parseInt(element.quantity),
          value_of_goods:
            parseFloat(element.price) * parseInt(element.quantity),
          currency_value_of_goods: 'EUR',
        }
      }),
    }
  }

  const optionalDeliveryValues = {}

  // pickup
  const pickup = { comments: '' }
  if (values.from.pickupSource !== PICKUP_OPTIONS.requestLater) {
    const minDate = new Date(values.from.pickup.split('/')[0])
    const maxDate = new Date(values.from.pickup.split('/')[1])

    pickup.is_fixed_pickup =
      values.from.addressSource === ADDRESS_SOURCES.pickUpPoint
        ? false
        : values.from.pickupSource === PICKUP_OPTIONS.fixedPickUp
    pickup.date =
      values.from.pickup &&
      values.from.addressSource !== ADDRESS_SOURCES.pickUpPoint
        ? format(minDate, 'yyyy-MM-dd')
        : ''
    pickup.time = [
      {
        min_hourminute: format(minDate, 'HH:mm'),
        max_hourminute: format(maxDate, 'HH:mm'),
      },
    ]
  }
  
  const shipping = {
    ...optionalShippingValues,
    client_center_code: values.from.imputationCenter.id,
    shipping_type_code: values.shipment.serviceType.originalCode,
    department_code: null,
    client_bar_code: '',
    shipping_weight_declared: getPackagesTotalWeight(values.shipment.packages),
    item_count: values.shipment.packages.length,
    shipping_date: getToday(),
    commodity_type_code: values.shipment.commodityType,

    sender_name: isOriginNewAddress(originAddressSource)
      ? values.from.address.name
      : values.from.selectedAddress.name,
    sender_country_code:
      values.from.addressSource === ADDRESS_SOURCES.pickUpPoint
        ? 'ES'
        : values.from.addressSource === 'newAddress'
        ? values.from.address.country.code
        : values.from.selectedAddress.shippingAddress.country,
    sender_postal_code:
      values.from.addressSource === ADDRESS_SOURCES.pickUpPoint
        ? '28821'
        : values.from.addressSource === 'newAddress'
        ? values.from.address.postalCode
        : values.from.selectedAddress.shippingAddress.postalCode,
    sender_address:
      values.from.addressSource === ADDRESS_SOURCES.pickUpPoint
        ? 'AVENIDA EUROPA'
        : createFullAddress(values.from),
    sender_address_2:
      values.from.addressSource === ADDRESS_SOURCES.pickUpPoint
        ? 'Coslada'
        : values.from.addressSource === 'newAddress'
        ? values.from.address.city
        : values.from.selectedAddress.shippingAddress.city,

    recipient_name: values.to.address.name,
    recipient_country_code:
      isDestinNewAddress(destinAddressSource) ||
      isDestinPickupPoint(destinAddressSource)
        ? values.to.address.country.code ?? values.to.address.country
        : values.from.selectedAddress.shippingAddress.country,
    recipient_postal_code: values.to.address.postalCode,
    recipient_address: isDestinNewAddress(destinAddressSource)
      ? createDestinFullAddress(values.to)
      : values.to.address.address,
    recipient_address_2: values.to.address.city,
    //recipient_state_or_province: values.to.address.province,
    recipient_national_id_number: '',

    pickup,

    delivery: {
      ...optionalDeliveryValues,
      comments: values.to.comments,
      contact_name:
        values.to.addressState === ADDRESS_STATES.AT_PICKUP_POINT
          ? values.to.address.contactName
          : '',
    },

    additionals: getAdditionals(
      values.services,
      values.to.addressState === ADDRESS_STATES.AT_PICKUP_POINT,
      values.to.address.organicPointCode,
      values.from.addressSource === ADDRESS_SOURCES.pickUpPoint,
    ),
    items: getShipmentPackages(values.shipment.packages),
    audit: auditData,
  }
  return shipping
}

export default formatSubmitData
